<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <ModuleShell
    :title="$t('views.selectedGoat.modules.temperature')"
    :icon="Thermometer"
    @click="goToTemperatureView"
    class="cursor-pointer"
  >
    <div v-if="temperatureStore.loading" class="text-sm opacity-50 text-center">
      {{ $t('views.selectedGoat.loading') }}
    </div>

    <!-- Display current temperature if available -->
    <div v-else-if="latestTemperature" class="flex items-center justify-between">
      <div>
        <p class="text-sm text-[var(--color-text)] opacity-70">
          {{ $t('views.selectedGoat.temperature.current') }}
        </p>
        <p class="text-2xl font-bold text-[var(--color-primary-600)]">
          {{ latestTemperature.value }} °C
        </p>
      </div>
      <div class="text-right">
        <p class="text-xs text-[var(--color-text)] opacity-50">
          {{ formatDate(latestTemperature.createdAt) }}
        </p>
      </div>
    </div>

    <!-- No temperature data -->
    <div v-else class="text-sm opacity-70 text-center py-2">
      {{ $t('views.selectedGoat.temperature.noData') }}
    </div>
  </ModuleShell>
</template>


<script setup>
import { onMounted, computed } from 'vue'
import { useTemperatureStore } from '@/stores/temperature.store.js'
import { storeToRefs } from 'pinia'
import ModuleShell from '@/components/ui/layout/ModuleShell.vue'
import { useRouter } from 'vue-router'
import { Thermometer } from 'lucide-vue-next'
import { formatDate } from '@/utils/format/dateformatting'

const props = defineProps({
  goatId: { type: Number, required: true }
})

const router = useRouter()
const temperatureStore = useTemperatureStore()
const { temperatures } = storeToRefs(temperatureStore)

// Get the latest temperature (most recent)
const latestTemperature = computed(() => {
  if (!temperatures.value || temperatures.value.length === 0) return null
  // Temperatures are already sorted DESC by created_at from repository
  return temperatures.value[0]
})

function goToTemperatureView() {
  if (!props.goatId) return
  router.push({ name: 'Temperature', params: { id: props.goatId } })
}

// Fetch temperatures if mounted
onMounted(() => {
  if (props.goatId) temperatureStore.fetchTemperatures(props.goatId)
})
</script>

<style scoped>
</style>
